import { createContext, useEffect, useState } from 'react';
import { ApplicationResponse, Application, ApplicationsResponse, PayloadResponse, RegistrationTypesResponse, RegistrationType, Branch, CountriesResponse, ProvincesResponse, CantonsResponse, DistricsResponse, Country, District, Canton, Province, MCCResponse, TariffsResponse, ClassifiersResponse, Mcc, Tariff, Classifier, ClientResponse, BranchesResponse, OfficersResponse, Officer, ZipCodeResponse } from '../../models/interfaces';
import topApi from '../../components/api/ToPApi';
import { Client, TreeNodeModel } from '../../components/tree/types';
import { mapPayloadToTree } from '../../components/tree/clientToTreeNodeModel';




type ApplicationsContextProps = {
    applications: Application[];
    isLoading: boolean;
    loadApplications: (statusList: string[]) => Promise<void>;
    getReport: (
        status: string,
        appId: string,
        ruc: string,
        businessName: string,
        dateIni: string,
        dateEnd: string) => Promise<void>;
    getResponse: (
        appId: string,
        ruc: string,
        businessName: string,
        dateIni: string,
        dateEnd: string) => Promise<string>;

    loadRegistrationTypes: () => Promise<RegistrationType[]>;
    loadBranches: () => Promise<Branch[]>;
    getApplication: (requestId: number) => Promise<TreeNodeModel>;
    addApplication: (application: TreeNodeModel, isNew: boolean) => Promise<[string, string?]>;
    updateApplication: (application: TreeNodeModel, entityId: string) => Promise<string>;
    loadCountries: () => Promise<Country[]>;
    loadProvinces: (countryId: number) => Promise<Province[]>;
    loadCantons: (countryId: number, provinceId: number) => Promise<Canton[]>;
    loadDistricts: (countryId: number, provinceId: number, cantonId: number) => Promise<District[]>;
    getZipCode: (countryId: number, provinceId: number, cantonId: number, districId: number) => Promise<string>;
    getMCC: () => Promise<Mcc[]>;
    getTariffPlan: () => Promise<Tariff[]>;
    getClassifiers: () => Promise<Classifier[]>;
    submitForApproval: (entityId: string) => Promise<string>;
    approveRequest: (entityId: string) => Promise<string>;
    rejectRequest: (entityId: string, reason: string) => Promise<string>;
    deleteRequest: (entityId: string, reason: string) => Promise<string>;
    getClient: (request: any) => Promise<Application>;
    getAccountOfficers: () => Promise<Officer[]>;





}



export const ApplicationsContext = createContext({} as ApplicationsContextProps);

export const ApplicationsProvider = ({ children }: any) => {

    const [applications, setApplications] = useState<Application[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [mccs, setMccs] = useState<Mcc[]>([]);
    const [classifiers, setClassifiers] = useState<Classifier[]>([]);
    const [plans, setPlans] = useState<Tariff[]>([]);



    const loadRegistrationTypes = async () => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<RegistrationTypesResponse>('/application/getRegistrationType');
            if (resp.status == 200) {
                return resp.data.registrationTypes;
            }
            setIsLoading(false);
        } catch (error) {
            return []
        }
    }

    const loadBranches = async () => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<BranchesResponse>('/branch/getBranchByUser');
            if (resp.status == 200) {
                return resp.data.branches;
            }
            setIsLoading(false);
        } catch (error) {
            return []
        }
    }


    const loadApplications = async (statusList) => {

        try {
            setIsLoading(true);
            setApplications([]);

            let apps = [];
            for (const status of statusList) {
                const resp = await topApi.get<ApplicationsResponse>('/application/' + status);
                if (resp.status == 200) {
                    apps.push(...resp.data.applications);

                }
            }
            setApplications(apps);


        } catch (error) {

        } finally {
            setIsLoading(false);
        }
    }

    const getApplication = async (appId) => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<PayloadResponse>('/application/getApplication/' + appId);
            if (resp.status == 200) {
                return resp.data.payload;
            }

        } catch (error) {

        } finally {
            setIsLoading(false);
        }
    }

    const getClient = async (req) => {

        try {
            setIsLoading(true);
            //const json = JSON.stringify(req);

            const resp = await topApi.post<ClientResponse>('/application/getClient', req);
            if (resp.status == 200) {
                const tree = mapPayloadToTree(resp.data);

                const response: Application = {
                    MERCHANT_RUC: (tree.entityData as Client).registrationNumber,
                    BUSINESS_NAME: (tree.entityData as Client).companyName,
                    STATUS: "APPROVED",
                    CONTACT_PERSON: "",
                    CONTACT_EMAIL: null,
                    APP_ID: -1,
                    BANK_CODE: "",
                    CREATED_AT: new Date(),
                    CREATED_BY: "",
                    CITY: null,
                    REJECTION_REASON: "",
                    payload: tree

                }
                return response;
            }

        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }
    }

    const addApplication = async (application: TreeNodeModel, isNew: boolean): Promise<[string, string?]> => {
        try {
            setIsLoading(true);

            const client = application.entityData as Client;

            let info = {
                merchantRuc: client.registrationNumber,
                merchantName: client.companyName,
                payload: application
            }

            const resp = await topApi.post<ApplicationResponse>('/application/', { app: info, isNew: isNew });

            if (resp.status == 200) {
                //application.entityId = resp.data.id.toString();
                setIsLoading(false);
                return ['success', resp.data.id.toString()]
            }

            setIsLoading(false);
            return ['Ocurrió un error de comunicación, intente de nuevo.', null]

        } catch (e) {
            const error = e.response?.data?.error;
            return [error || "Ocurrió un error de comunicación, intente de nuevo.", null];
        }
    }

    const updateApplication = async (application: TreeNodeModel, entityId: string) => {
        try {
            setIsLoading(true);

            let info = {
                payload: application
            }

            const resp = await topApi.put<ApplicationResponse>('/application/' + entityId, { app: info });

            if (resp.status == 200) {
                setIsLoading(false);
                return 'success'
            }

            setIsLoading(false);
            return 'Ocurrió un error de comunicación, intente de nuevo.'

        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    const loadCountries = async () => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<CountriesResponse>('/location/getCountries');
            if (resp.status == 200) {
                return resp.data.countries;
            }
            setIsLoading(false);
        } catch (error) {
            return []
        }
    }

    const loadProvinces = async (countryId: number) => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<ProvincesResponse>('/location/getProvinces/' + countryId);
            if (resp.status == 200) {
                return resp.data.provinces;
            }
            setIsLoading(false);
        } catch (error) {
            return []
        }
    }

    const loadCantons = async (countryId: number, provinceId: number) => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<CantonsResponse>('/location/getCantons/' + countryId + '/' + provinceId);
            if (resp.status == 200) {
                return resp.data.cantons;
            }
            setIsLoading(false);
        } catch (error) {
            return []
        }
    }

    const loadDistricts = async (countryId: number, provinceId: number, cantonId: number) => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<DistricsResponse>('/location/getDistricts/' + countryId + '/' + provinceId + '/' + cantonId);
            if (resp.status == 200) {
                return resp.data.districts;
            }
            setIsLoading(false);
        } catch (error) {
            return []
        }
    }

    const getZipCode = async (countryId: number, provinceId: number, cantonId: number, districId: number) => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<ZipCodeResponse>('/location/getZipCode/' + countryId + '/' + provinceId + '/' + cantonId + '/' + districId);
            if (resp.status == 200) {
                return resp.data.zipCode;
            }
            setIsLoading(false);
        } catch (error) {
            return "00000"
        }
    }

    const getMCC = async () => {

        try {

            if (mccs.length == 0) {
                setIsLoading(true);
                const resp = await topApi.get<MCCResponse>('/application/getMCC');
                if (resp.status == 200) {
                    setMccs(resp.data.mccs);
                    return resp.data.mccs;
                }
                setIsLoading(false);
            } else {
                return mccs
            }


        } catch (error) {
            return []
        }
    }

    const getTariffPlan = async () => {

        try {
            if (plans.length > 0) {
                return plans;
            }
            setIsLoading(true);
            const resp = await topApi.get<TariffsResponse>('/application/getTariffPlan');
            if (resp.status == 200) {
                //setPlans(resp.data.tariffs);
                //return resp.data.tariffs;

                const sortedTariffs = resp.data.tariffs.sort((a, b) =>
                    a.valueName.localeCompare(b.valueName)
                );

                setPlans(sortedTariffs);
                return sortedTariffs;
            }
            setIsLoading(false);
        } catch (error) {
            return []
        }
    }

    const getClassifiers = async () => {

        try {
            if (classifiers.length > 0) {
                return classifiers;
            }
            setIsLoading(true);
            const resp = await topApi.get<ClassifiersResponse>('/application/getClassifiers');
            if (resp.status == 200) {
                setClassifiers(resp.data.classifiers);
                return resp.data.classifiers;
            }
            setIsLoading(false);
        } catch (error) {
            return []
        }
    }

    const submitForApproval = async (entityId: string) => {
        try {
            setIsLoading(true);
            const resp = await topApi.post<ApplicationResponse>('/application/submitForApproval/' + entityId, {});
            if (resp.status == 200) {
                setIsLoading(false);
                return 'success'
            }

            setIsLoading(false);
            return 'Ocurrió un error de comunicación, intente de nuevo.'

        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    const approveRequest = async (entityId: string) => {
        try {
            setIsLoading(true);
            const resp = await topApi.post<ApplicationResponse>('/application/approveRequest/' + entityId, {});
            if (resp.status == 200) {
                setIsLoading(false);
                return 'success'
            }

            setIsLoading(false);
            return 'Ocurrió un error de comunicación, intente de nuevo.'

        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    const rejectRequest = async (entityId: string, reason: string) => {
        try {
            setIsLoading(true);
            const resp = await topApi.post<ApplicationResponse>('/application/rejectRequest/' + entityId, { reason });
            if (resp.status == 200) {
                setIsLoading(false);
                return 'success'
            }

            setIsLoading(false);
            return 'Ocurrió un error de comunicación, intente de nuevo.'

        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    const deleteRequest = async (entityId: string, reason: string) => {
        try {
            setIsLoading(true);
            const resp = await topApi.post<ApplicationResponse>('/application/deleteRequest/' + entityId, { reason });
            if (resp.status == 200) {
                setIsLoading(false);
                return 'success'
            }

            setIsLoading(false);
            return 'Ocurrió un error de comunicación, intente de nuevo.'

        } catch (e) {
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    const getReport = async (status, appId, ruc,
        businessName,
        dateIni,
        dateEnd) => {

        try {
            setIsLoading(true);
            setApplications([]);

            const req = {
                "dateIni": dateIni,
                "dateEnd": dateEnd,
                "appId": appId,
                "appRegistration": ruc,
                "registrationName": businessName
            };
            const resp = await topApi.post<ApplicationsResponse>('/application/getReport', req);
            if (resp.status == 200) {

                const mappedApplications: Application[] = resp.data.applications.map((app: any) => ({
                    APP_ID: Number(app.appId),
                    BANK_CODE: "", // no viene en respuesta
                    MERCHANT_RUC: app.appRegistration,
                    BUSINESS_NAME: app.appRegName,
                    STATUS: app.statusId,
                    CREATED_AT: new Date(app.createdAt),
                    CREATED_BY: app.createdBy,
                    CHECKED_AT: app.checkedAt,
                    APPROVED_BY: app.approvedBy,
                    CONTACT_PERSON: "",
                    CONTACT_EMAIL: null,
                    CITY: null,
                    REJECTION_REASON: "",
                    payload: {} as TreeNodeModel, // o null si lo permites
                    response: JSON.parse(app.response) // parsear la respuesta JSON a un objeto JavaScript
                }));
                setApplications(mappedApplications);
            }

        } catch (error) {

        } finally {
            setIsLoading(false);
        }
    }

    const getResponse = async (appId, ruc,
        businessName,
        dateIni,
        dateEnd) => {

        try {
            setIsLoading(true);

            const req = {
                "dateIni": dateIni,
                "dateEnd": dateEnd,
                "appId": appId,
                "appRegistration": ruc,
                "registrationName": businessName
            };
            const resp = await topApi.post<ApplicationsResponse>('/application/getReport', req);
            if (resp.status == 200) {
                const response = resp.data.applications[0].response;
                return JSON.parse(response);
            }
            return null;
        } catch (error) {
            return null;
        } finally {
            setIsLoading(false);
        }
    }


    const getAccountOfficers = async () => {

        try {
            setIsLoading(true);
            const resp = await topApi.get<OfficersResponse>('/application/getAccountOfficers');
            if (resp.status == 200) {
                return resp.data.officers;
            }
            setIsLoading(false);
        } catch (error) {
            return []
        }
    }




    return (
        <ApplicationsContext.Provider value={{
            applications, isLoading, loadApplications, addApplication, updateApplication,
            getApplication, loadRegistrationTypes, loadBranches, loadCountries, loadProvinces, loadCantons, loadDistricts,
            getMCC, getTariffPlan, getClassifiers, submitForApproval, rejectRequest, deleteRequest, approveRequest, getClient,
            getReport, getAccountOfficers, getZipCode, getResponse
        }}>
            {children}
        </ApplicationsContext.Provider>
    );
}